/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unsafe-argument -- pcg-core surface is loose-typed by design */
/* eslint-disable @typescript-eslint/no-explicit-any -- guide payload and pcg-core are loose-typed by design */

// JSX rewrite of the prototype's render function: the audit panel (core-viewer.js renderAudit, 943-1151) as
// JSX: integrity summary, audit field search, embedded Reject File Gate panel,
// and the full audit JSON <details>.
//
// Deliberate deviations (logged in core/MODULE-MAP.md, same cleanup class as
// the vendored analysis gates):
//   1. The LAST TWO canonical-version allow-lists (source lines 1003 and 1083 —
//      `APP_NAME === 'PCG Exam Viewer' && [19-version list].includes(APP_VERSION)`)
//      are DELETED. Canonical exam-viewer mode is hard-enabled everywhere else
//      in the port, so: the `canonical_sd_ratio` audit note is always the
//      canonical text, and the v0.7.1 integrity-summary/search tools always
//      render. Consequence: the "Full audit JSON" <details> is never default-
//      open (the prototype opened it only when the tools were absent, i.e. only
//      in the never-shipped legacy branch).
//   2. Missing turbulent model is an ERROR STATE (the logistic fallback lands
//      on the wrong side of its threshold in 16 of 17 scored fixture-corpus
//      recordings). The prototype silently reported the fallback's
//      name/score here; this panel refuses to compute a score without the
//      serialized forest and says so, in the JSON and the summary grid.
//   3. The prototype re-bound the search <input> listener on every renderAudit
//      call (leaking handlers); the search is plain React state here.
//   4. Timing engine (upstream v0.9.1 m1acq): the JSON carries the export
//      package's `evaluation_range` provenance block (the range, the engine
//      package, the calculation methods and the engine's S1-to-S1 interval
//      CV, described as an internal stability / confidence measure), and when
//      the engine is present but not ready the reported timing is withheld,
//      as the Provisional Results and the export package withhold it.

import React, { ReactElement, useMemo, useState } from "react"
import { fmtNumber } from "../core/format"
import { MURMUR_GUIDE_THRESHOLD, TURBULENT_EDGE_TRIM_S, MIN_ENERGY_RELIABLE_CYCLES } from "../core/constants"
import { turbulentModelName, turbulentModelSource, turbulentDecisionThreshold } from "../core/turbulence"
import { rejectGate } from "../core/gate"
import { ENGINE_RATIO_CALCULATION, S1_INTERVAL_CV_DESCRIPTION, evaluationRangeProvenance } from "../core/timing"
import { ENGINE_UNAVAILABLE } from "../core/evaluation-range"
import PcgRejectGatePanel from "./reject-gate-panel"

const TURBULENT_MODEL_MISSING_ERROR =
  "Turbulent-energy model not provided; research turbulence score not computed. " +
  "The logistic fallback is disallowed for display (it inverts the flag on 16 of 17 scored fixture-corpus recordings)."

// core-viewer.js 1121-1145, verbatim: line-based JSON search with object/array
// block expansion for `key: {`-style matches.
function auditSearchMatches(auditPayload: string, query: string): string[] {
  const lines = auditPayload.split("\n")
  const matches: string[] = []
  lines.forEach((line, index) => {
    if (line.toLowerCase().includes(query)) {
      const trimmed = line.trim()
      const objectStart = /:\s*[{[]\s*$/.test(trimmed)
      if (objectStart) {
        let depth = 0
        let end = index
        for (let cursor = index; cursor < lines.length; cursor++) {
          const candidate = lines[cursor]
          depth += (candidate.match(/[{[]/g) || []).length
          depth -= (candidate.match(/[}\]]/g) || []).length
          end = cursor
          if (cursor > index && depth <= 0) break
        }
        matches.push(lines.slice(index, end + 1).join("\n"))
      } else {
        const start = Math.max(0, index - 1)
        const end = Math.min(lines.length, index + 2)
        matches.push(lines.slice(start, end).join("\n"))
      }
    }
  })
  return [...new Set(matches)].slice(0, 100)
}

const IntegrityCell = ({
  label,
  value,
  valueStyle,
  large = true,
}: {
  label: string
  value: React.ReactNode
  valueStyle?: React.CSSProperties
  large?: boolean
}): ReactElement => (
  <div className="audit-integrity-cell">
    <div className="audit-integrity-label">{label}</div>
    <div className={`audit-integrity-value${large ? "" : " small"}`} style={valueStyle}>
      {value}
    </div>
  </div>
)

const PcgAuditPanel = ({
  core,
  showRejectGate = true,
  energyBand = "s100",
}: {
  core: any
  /**
   * The prototype embeds the Reject File Gate panel inside the audit panel
   * (renderAudit line 1104). Hosts that surface PcgRejectGatePanel as its own
   * first-class section can pass false to avoid rendering it twice.
   */
  showRejectGate?: boolean
  /**
   * dev.18: research_comparison.coherent_phase_energy_v1 surfaces the Coherent
   * Energy result for the viewer's selected sweep band (default matches the
   * prototype's bootstrap render, before recommended-band initialization).
   */
  energyBand?: string
}): ReactElement => {
  const [searchQuery, setSearchQuery] = useState("")

  const { auditPayload, reported, alignment, quality } = useMemo(() => {
    const D = core.ctx.payload
    const pkg = D.examPackage || {}
    const markers = (D.peaks || []).map((p: any) => ({
      t: p.t,
      label: p.l,
      source: p.source || "direct",
      confidence: p.confidence,
      guideOnly: !!p.guideOnly,
      // Timing-engine markers (upstream v0.9.1): outside the Evaluation Range, shown for context only
      outsideEvalRange: p.outsideEvalRange === true,
      usedForResults: p.usedForResults !== false,
    }))
    const acousticActivity = core.visualAcousticActivityStats()
    const turbulentModelMissing = !core.ctx.turbulentModel
    // Deviation 2: never compute (and hence never display) the fallback score.
    const turbulenceEvidence = turbulentModelMissing ? null : core.structuredTurbulenceEvidence()
    const phaseRisk = core.phaseRiskConfidence()
    const nrpeActivity = core.visualAcousticActivityStats(false)
    const nrpeExperiment = core.nrpeExperimentSchema(nrpeActivity)
    // dev.18 research comparison inputs (research-only surfaces).
    const coherentEnergy = core.coherentEnergyV1(energyBand)
    const gateForResearch = rejectGate(D)
    const timing = core.timingConsistency()
    const stats = core.timingStats()
    const q = core.analysisQuality()
    const inferredMarkerSummary = {
      detected: markers.filter((m: any) => m.source !== "inferred").length,
      inferred_metric_bearing: markers.filter((m: any) => m.source === "inferred" && !m.guideOnly).length,
      inferred_visual_only: markers.filter((m: any) => m.source === "inferred" && Boolean(m.guideOnly)).length,
      context_outside_eval_range: markers.filter((m: any) => Boolean(m.outsideEvalRange) || !m.usedForResults).length,
    }
    const evaluationRange = evaluationRangeProvenance(core.ctx)
    // Display contract: every reported timing number is gated
    // on analysisQuality().summaryAllowed — timingStats() alone keeps emitting
    // numbers for recordings the client has declared unreliable — and on the
    // timing engine, when present, being ready (deviation 4).
    const withheld = core.ctx.engine?.status === ENGINE_UNAVAILABLE
    const reportable = q.summaryAllowed && !withheld
    const exactMs = (value: unknown): number | null =>
      !withheld && Number.isFinite(Number(value)) ? Number(value) : null
    const rep = {
      bpm: reportable && stats ? Number(stats.hr) : null,
      regular_pct:
        reportable && timing && typeof timing.closePct === "number" ? Number(timing.closePct.toFixed(1)) : null,
      sys_ms: reportable && stats && stats.sys !== "-" ? Number(stats.sys) : null,
      dia_ms: reportable && stats && stats.dia !== "-" ? Number(stats.dia) : null,
      sd_ratio: reportable && stats && stats.ratio !== "-" ? Number(stats.ratio) : null,
      sd_ratio_display:
        reportable && stats && Number(stats.ratio) > 0 ? `1 : ${(1 / Number(stats.ratio)).toFixed(1)}` : null,
      sd_ratio_calculation: !withheld && stats?.ratioCalculation ? stats.ratioCalculation : null,
      mean_systolic_ms_exact: exactMs(stats?.sysExactMs),
      mean_diastolic_ms_exact: exactMs(stats?.diaExactMs),
      selected_timing_cycle_ids: q.selectedTimingCycleIds || [],
    }
    const allDetected = {
      bpm: D.metrics && typeof D.metrics.hr_bpm === "number" ? D.metrics.hr_bpm : null,
      regular_index: timing && typeof timing.index === "number" ? timing.index : null,
      cycle_variability: timing && typeof timing.cv === "number" ? timing.cv : null,
      rr_ms: D.metrics && typeof D.metrics.rr_ms === "number" ? D.metrics.rr_ms : null,
      sys_ms: D.metrics && typeof D.metrics.sys_ms === "number" ? D.metrics.sys_ms : null,
      dia_ms: D.metrics && typeof D.metrics.dia_ms === "number" ? D.metrics.dia_ms : null,
      sd_ratio: D.metrics && typeof D.metrics.sd_ratio === "number" ? D.metrics.sd_ratio : null,
      coverage: D.metrics && typeof D.metrics.coverage === "number" ? D.metrics.coverage : null,
      pct_high_conf: D.metrics && typeof D.metrics.pct_high_conf === "number" ? D.metrics.pct_high_conf : null,
      n_low_conf: D.metrics && typeof D.metrics.n_low_conf === "number" ? D.metrics.n_low_conf : null,
    }
    const payloadJson = JSON.stringify(
      {
        package: {
          schema: pkg.schema,
          version: pkg.version,
          audio: pkg.audio && { kind: pkg.audio.kind, name: pkg.audio.name, sample_rate: pkg.audio.sample_rate },
          provenance: pkg.provenance,
          model_version: pkg.model_version,
          uncertainty: pkg.uncertainty,
          hcp_panel_status: pkg.hcp_panel_status,
        },
        // dev.18: research-only comparison surfaces (never a released finding).
        research_comparison: {
          released_input_gate_decision: gateForResearch && gateForResearch.gate_decision,
          alternative_input_gate_v1: gateForResearch && gateForResearch.research_alternative_gate_v1,
          coherent_phase_energy_v1: coherentEnergy && {
            schema: coherentEnergy.schema,
            status: coherentEnergy.status,
            selected_timing_cycle_ids: coherentEnergy.selectedTimingCycleIds,
            research_scored_timing_cycle_ids: coherentEnergy.scoredTimingCycleIds,
            timing_basis: coherentEnergy.timingBasis,
            accepted_timing_coverage_pct: coherentEnergy.acceptedTimingCoveragePct,
            research_timing_coverage_pct: coherentEnergy.researchTimingCoveragePct,
            morphology_recovered: coherentEnergy.morphologyRecovered,
            morphology_phase: coherentEnergy.morphologyPhase,
            morphology_cluster_cycles: coherentEnergy.morphologyClusterCycles,
            morphology_feature_bands_hz: coherentEnergy.morphologyFeatureBands,
            scored_cycles: coherentEnergy.scoredCycles,
            required_cycle_hits: coherentEnergy.requiredCycleHits,
            sys_recurrence_pct: coherentEnergy.sysRecurrencePct,
            dia_recurrence_pct: coherentEnergy.diaRecurrencePct,
            coherent_phase_coverage_pct: coherentEnergy.coherentPhaseCoveragePct,
            proposed_sys: coherentEnergy.proposedSys,
            proposed_dia: coherentEnergy.proposedDia,
            reason: coherentEnergy.reason,
          },
        },
        reported_timing: rep,
        // Timing-engine guides only
        ...(evaluationRange ? { evaluation_range: evaluationRange } : {}),
        all_cycle_timing: allDetected,
        audit_notes: {
          all_cycle_timing: "Includes detected cycles that were not used in the reported timing summary.",
          inferred_markers:
            "Metric-bearing inferred markers may contribute to timing cycles. Visual-only inferred markers complete the guide display but do not change Cardiac Report findings.",
          energy_cycle_alignment:
            "Energy v1 scores the exact selected Timing cycle IDs. It does not choose an independent best Energy cycle run.",
          // Deviation 1 (source line 1003): version allow-list deleted; the
          // canonical note is unconditional (legacy branch never shipped),
          // worded for the engine's medians when a timing-engine guide
          // supplied them.
          canonical_sd_ratio:
            stats.ratioCalculation === ENGINE_RATIO_CALCULATION
              ? "S/D is the ratio of the timing engine's median systolic duration to its median diastolic duration over the Evaluation Range; display and export use this same value."
              : "S/D is the ratio of mean systolic duration to mean diastolic duration across the accepted Timing cycle IDs; display and export use this same value.",
          ...(evaluationRange ? { s1_interval_cv: S1_INTERVAL_CV_DESCRIPTION } : {}),
        },
        inferred_marker_summary: inferredMarkerSummary,
        metrics: D.metrics || {},
        timing_consistency: timing && {
          label: timing.label,
          index: timing.index,
          cycle_variability: timing.cv,
          close_cycle_pct: timing.closePct,
          outlier_cycles: timing.outlierCount,
          basis: "S1-to-S1 cycle regularity",
        },
        visual_acoustic_activity: {
          assumptions: {
            band_hz: [100, 300],
            cycle_threshold: MURMUR_GUIDE_THRESHOLD,
            edge_trim_s: TURBULENT_EDGE_TRIM_S,
            edge_trim_behavior: "audit_only_for_aligned_energy_v1",
            purpose: "Sensitive visual review overlay; not a classifier.",
          },
          cycle_alignment: q.energyCycleAlignment || null,
          output: acousticActivity && {
            scored_cycles: acousticActivity.cycles,
            scored_cycle_ids: acousticActivity.cycleAlignment ? acousticActivity.cycleAlignment.scoredCycleIds : [],
            selected_timing_cycle_ids: q.selectedTimingCycleIds || [],
            sys_cycle_pct: acousticActivity.sysCyclePct,
            dia_cycle_pct: acousticActivity.diaCyclePct,
            sys_mean: acousticActivity.sysMean,
            dia_mean: acousticActivity.diaMean,
            sys_peak: acousticActivity.sysPeak,
            dia_peak: acousticActivity.diaPeak,
            sys_peaks_per_cycle: acousticActivity.features.sys_peaks_per_cycle,
            dia_peaks_per_cycle: acousticActivity.features.dia_peaks_per_cycle,
          },
        },
        phase_energy_display: {
          schema: "medaica.pcg.phase_energy_display.v1",
          visual_energy_present: !!q.murmurBandEnergyEvidence?.detected,
          murmur_energy_detected: !!q.murmurBandEnergyEvidence?.detected,
          murmur_energy_substantial: !!q.murmurBandEnergyEvidence?.substantial,
          murmur_energy_evidence_source: "viewer_murmur_band_energy_evidence",
          murmur_energy_evidence: q.murmurBandEnergyEvidence || null,
          selected_timing_cycle_count: q.selectedTimingCycleCount || 0,
          selected_timing_cycle_ids: q.selectedTimingCycleIds || [],
          scored_energy_cycle_ids: acousticActivity?.cycleAlignment
            ? acousticActivity.cycleAlignment.scoredCycleIds
            : [],
          cycle_ids_aligned:
            !!acousticActivity?.cycleAlignment &&
            JSON.stringify(acousticActivity.cycleAlignment.scoredCycleIds || []) ===
              JSON.stringify(q.selectedTimingCycleIds || []),
          phase_energy_min_selected_cycles: q.phaseEnergyMinSelectedCycles || MIN_ENERGY_RELIABLE_CYCLES,
          phase_energy_status: q.phaseEnergyStatus || "unavailable",
          phase_assignment_supported: q.phaseEnergyStatus === "scored" && !q.visualEnergyPhaseLimited,
          phase_assignment_limited: q.phaseEnergyStatus === "limited_visible_energy" || !!q.visualEnergyPhaseLimited,
          phase_energy_unavailable_reason:
            q.phaseEnergyStatus === "scored" ? null : q.visualEnergyReason || q.energyReason || null,
        },
        structured_turbulence_evidence: {
          assumptions: {
            purpose: "Research evidence scoring; not shown as diagnostic truth.",
            model: turbulentModelMissing ? "unavailable" : turbulentModelName(core.ctx.turbulentModel),
            model_source: turbulentModelMissing ? "unavailable" : turbulentModelSource(core.ctx.turbulentModel),
            decision_threshold: turbulentModelMissing ? null : turbulentDecisionThreshold(core.ctx.turbulentModel),
          },
          // Deviation 2: explicit error instead of a silently-fallback score.
          model_error: turbulentModelMissing ? TURBULENT_MODEL_MISSING_ERROR : null,
          output: turbulenceEvidence && {
            score: turbulenceEvidence.score,
            score_flag: turbulenceEvidence.scoreFlag,
            scored_cycles: turbulenceEvidence.cycles,
            sys_cycle_pct: turbulenceEvidence.sysCyclePct,
            dia_cycle_pct: turbulenceEvidence.diaCyclePct,
            feature_map: turbulenceEvidence.featureMap,
          },
        },
        normalized_reference_phase_energy_experiment: nrpeExperiment,
        phase_risk: phaseRisk,
        guide_markers: markers,
        analysis_quality: q,
      },
      null,
      2
    )
    return {
      auditPayload: payloadJson,
      reported: rep,
      alignment: q.energyCycleAlignment || {},
      quality: q,
    }
  }, [core, energyBand])

  const query = searchQuery.trim().toLowerCase()
  const matches = useMemo(() => (query ? auditSearchMatches(auditPayload, query) : []), [auditPayload, query])
  const searchStatus = !query
    ? "Enter a field or value"
    : matches.length
    ? `${matches.length} match${matches.length === 1 ? "" : "es"}`
    : "No matches"

  const sdCalculationLabel =
    reported.sd_ratio_calculation === "ratio_of_mean_accepted_cycle_durations"
      ? "Ratio of mean accepted-cycle durations"
      : reported.sd_ratio_calculation || "Unavailable"

  // Durations from the timing engine are medians, from the cycle calculation means
  const durationsLabel =
    reported.sd_ratio_calculation === ENGINE_RATIO_CALCULATION ? "Median durations" : "Mean durations"

  return (
    <div className="audit-panel">
      {/* Deviation 1 (source line 1083): the v0.7.1 integrity-summary/search
          tools render unconditionally — the version allow-list is deleted. */}
      <section className="audit-integrity">
        <div className="audit-integrity-title">Integrity summary</div>
        <div className="audit-integrity-grid">
          <IntegrityCell label="S/D calculation" value={sdCalculationLabel} large={false} />
          <IntegrityCell
            label="Exact S/D ratio"
            value={`${reported.sd_ratio ?? "N/A"} · ${reported.sd_ratio_display || "N/A"}`}
          />
          <IntegrityCell
            label={durationsLabel}
            value={`SYS ${fmtNumber(reported.mean_systolic_ms_exact, 2)} ms · DIA ${fmtNumber(
              reported.mean_diastolic_ms_exact,
              2
            )} ms`}
          />
          <IntegrityCell label="Selected Timing cycles" value={(reported.selected_timing_cycle_ids || []).length} />
          <IntegrityCell
            label="Energy cycle alignment"
            value={alignment.aligned ? "Aligned" : "Not scored / check audit"}
            valueStyle={{ color: alignment.aligned ? "#86efac" : "#fbbf24" }}
          />
          <IntegrityCell label="Phase Energy status" value={quality.phaseEnergyStatus || "unavailable"} />
        </div>
        <div className="audit-search-row">
          <label htmlFor="pcg-audit-field-search" className="audit-search-label">
            Search audit
          </label>
          <input
            id="pcg-audit-field-search"
            className="audit-search-input"
            type="search"
            placeholder="e.g. selected_timing_cycle_ids"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <span className="audit-search-status">{searchStatus}</span>
        </div>
        {matches.length ? <pre className="audit-json audit-search-results">{matches.join("\n…\n")}</pre> : null}
      </section>
      {showRejectGate ? <PcgRejectGatePanel payload={core.ctx.payload} /> : null}
      <details style={{ marginTop: 10 }}>
        <summary className="audit-details-summary">Full audit JSON</summary>
        <pre className="audit-json">{auditPayload}</pre>
      </details>
    </div>
  )
}

export default PcgAuditPanel
